/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('./bootstrap');

window.Vue = require('vue').default;
window.moment = require('moment');

let Vue = window.Vue;

import Vuetify from 'vuetify';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import _ from 'lodash';
import store from './store/index';
import VueSignaturePad from 'vue-signature-pad';

Vue.use(Vuetify);
Vue.use(VueSignaturePad);

Object.defineProperty(Vue.prototype, '_', { value: _ });
Vue.prototype.moment = window.moment;

/**
 * The following block of code may be used to automatically register your
 * Vue components. It will recursively scan this directory for the Vue
 * components and automatically register them with their "basename".
 *
 * Eg. ./components/ExampleComponent.vue -> <example-component></example-component>
 */

// const files = require.context('./', true, /\.vue$/i)
// files.keys().map(key => Vue.component(key.split('/').pop().split('.')[0], files(key).default))

Vue.mixin(require(process.env.MIX_APP_ROOT + '/bgo/translations/resources/js/translateMixin.js').default);
Vue.mixin(require(process.env.MIX_APP_ROOT + '/bgo/bgo-components/resources/js/mixins/Permissions.js').default);

/**
 * Dynamic load app.js in modules
 */
const ownModulesFiles = require.context('../../modules/', true, /app\.js$/i);
ownModulesFiles.keys().map((key) => ownModulesFiles(key));

const vendorModulesFiles = require.context('../../bgo/', true, /app\.js$/i);

vendorModulesFiles.keys().map((key) => vendorModulesFiles(key));

/**
 * Sentry configuration initialization from the .env file.
 */
if (process.env.SENTRY_ENVIRONMENT) {
    Sentry.init({
        Vue,
        dsn: process.env.SENTRY_LARAVEL_DSN,
        environment: process.env.SENTRY_ENVIRONMENT,
        integrations: [
            new BrowserTracing({
                tracingOrigins: [process.env.SENTRY_ENVIRONMENT !== 'local' ? process.env.APP_URL : 'localhost', /^\//],
            }),
        ],
        tracesSampleRate: process.env.SENTRY_ENVIRONMENT !== 'local' ? process.env.SENTRY_TRACES_SAMPLE_RATE : 0,
        beforeSend(event, hint) {
            if (hint.originalException && hint.originalException.response) {
                const statusCode = hint.originalException.response.status;
                const message = hint.originalException.message;

                if (
                    statusCode === 401 ||
                    statusCode === 403 ||
                    message === 'this.$refs.recaptcha.reset is not a function'
                ) {
                    return null;
                }
            }
            return event;
        },
    });
}

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding compo3ents to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */

new Vue({
    store,
    vuetify: new Vuetify({
        theme: {
            themes: {},
        },
    }),
    el: '#app',
    data() {
        return {
            overlay: false,
            sessionDialog: false,
            defaultMandatoryAsteriks: '∗',
            themeOptions: {
                'outlined-grid-buttons': true,
                'outlined-grid-chips': false,
                rounded: true,
            },
            menuOpenManual: false,
            menu: true,
            menuGroups: {},
            adminMenu: false,
            notifications: [],
            logoutDialog: false,
            translations: window.translations,
            language: document.getElementsByTagName('html')[0].getAttribute('lang'),
            themes: [
                {
                    id: 0,
                    name: 'Britannia Modern',
                    default: true,
                    scss: 'app.css',
                    dark: {
                        primary: '#3cacb6',
                        secondary: '#000000',
                        accent: '#05798b',
                        error: '#e8535f',
                        red: '#e8535f',
                        orange: '#d7ae55',
                        amber: '#d7ae55',
                        green: '#11b372',
                        success: '#11b372',
                        blue: '#1e79ad',
                        info: '#1e79ad',
                        notifications: '#4f2b8c',
                        purple: '#6967EC',
                    },
                    light: {
                        primary: '#3cacb6',
                        secondary: '#000000',
                        accent: '#05798b',
                        error: '#e8535f',
                        red: '#e8535f',
                        orange: '#d7ae55',
                        amber: '#d7ae55',
                        green: '#11b372',
                        success: '#11b372',
                        blue: '#1e79ad',
                        info: '#1e79ad',
                        notifications: '#4f2b8c',
                        purple: '#6967EC',
                    },
                },
                {
                    id: 1,
                    name: 'Britannia Classic',
                    default: false,
                    scss: 'classic.css',
                    dark: {
                        primary: '#3cacb6',
                        secondary: '#000000',
                        accent: '#05798b',
                        error: '#e8535f',
                        red: '#e8535f',
                        orange: '#d7ae55',
                        amber: '#d7ae55',
                        green: '#11b372',
                        success: '#11b372',
                        blue: '#1e79ad',
                        info: '#1e79ad',
                        notifications: '#4f2b8c',
                        purple: '#6967EC',
                    },
                    light: {
                        primary: '#3cacb6',
                        secondary: '#000000',
                        accent: '#05798b',
                        error: '#e8535f',
                        red: '#e8535f',
                        orange: '#d7ae55',
                        amber: '#d7ae55',
                        green: '#11b372',
                        success: '#11b372',
                        blue: '#1e79ad',
                        info: '#1e79ad',
                        notifications: '#4f2b8c',
                        purple: '#6967EC',
                    },
                },
            ],
            dateFormat: 'DD.MM.Y',
            dateFormatWithHours: 'DD.MM.Y hh:mm A',
            rules: {
                email: (value) => {
                    /* eslint-disable no-useless-escape */
                    const pattern =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return value ? pattern.test(value) || t('default::validation.email') : true;
                },
                phone: (value) => {
                    /* eslint-disable no-useless-escape */
                    const pattern = /^\+?[\d\s\-\(\)x\.]{6,20}$/;
                    return value ? pattern.test(value) || t('default::validation.phone') : true;
                },
                rpsgbNumber: (value) => {
                    const pattern = /^\+?[\d\s\-\(\)x\.]{3,30}$/;
                    return value ? pattern.test(value) || t('default::validation.rpsgb') : true;
                },
                integer: (value) => {
                    const pattern = /^[0-9]*$/;
                    return value ? pattern.test(value) || t('default::validation.integer') : true;
                },
                fax: (value) => {
                    const pattern = /^\+?[\d\s\-\(\)x\.]{6,20}$/;
                    return value ? pattern.test(value) || t('default::validation.fax') : true;
                },
                postCode: (value) => {
                    const pattern = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/;
                    const pattern2 =
                        /^(([A-Z]{1,2}[0-9][A-Z0-9]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?[0-9][A-Z]{2}|BFPO ?[0-9]{1,4}|(KY[0-9]|MSR|VG|AI)[ -]?[0-9]{4}|[A-Z]{2} ?[0-9]{2}|GE ?CX|GIR ?0A{2}|SAN ?TA1)$/;
                    return value
                        ? pattern.test(value) || pattern2.test(value) || t('default::validation.invalid_uk_post_code')
                        : true;
                },
            },
            breadcrumbs: [],
        };
    },
    computed: {
        menuOpen: {
            get() {
                if (this.$vuetify.breakpoint.xlOnly && this.menuOpenManual) {
                    return true;
                }

                if (this.menuOpenManual === false) {
                    return false;
                }

                return !!(this.$vuetify.breakpoint.xlOnly || this.menuOpenManual);
            },
            set() {
                return (this.menuOpenManual = true);
                //this.menuOpenManual = value;
            },
        },
        allThemes() {
            return this.$store.state.themes.all;
        },
        currentTheme() {
            return this.$store.state.themes.current;
        },
    },
    watch: {
        currentTheme(newTheme, oldTheme) {
            if (oldTheme !== newTheme) {
                this.changeTheme(newTheme);
            }
        },
        menu(newValue, oldValue) {
            localStorage.menu = newValue;
            if (newValue !== oldValue) {
                this.setWrapSize(newValue);
            }
        },
    },
    created() {
        const ignoredMessage = 'The .native modifier for v-on is only valid on components but it was used on <div>.';
        Vue.config.warnHandler = (message, vm, componentTrace) => {
            if (message !== ignoredMessage) {
                console.error(message + componentTrace);
            }
        };
    },
    mounted() {
        if (localStorage.menu) {
            this.menu = localStorage.menu === 'true';
        }
        let loader = document.getElementsByClassName('vld-overlay');
        if (loader[0] !== undefined) {
            loader[0].classList.remove('is-active');
        }
        this.setAllThemes();
        this.setCurrentTheme();
        this.setProfile(typeof User !== 'undefined' && User ? User : null);
        this.randomColor();
        this.initMenuGroups();
        this.simulateResize();
        if (
            process.env.HAS_SESSION_REFRESH &&
            !['0', 'false'].some((item) => item === process.env.HAS_SESSION_REFRESH)
        ) {
            this.keepAlive();
        } else {
            window.axios.interceptors.response.use(
                (response) => {
                    return response;
                },
                (error) => {
                    if (error.response && error.response.status === 401) {
                        this.sessionDialog = true;
                        //never resolving promise to suppress errors
                        return new Promise(() => {});
                    }
                    throw error;
                }
            );
        }
        if (this.$el.classList.contains('landing')) {
            this.goTo(route('auth.login'));
        }
    },
    methods: {
        keepAlive() {
            function ping() {
                let refreshSessionAfter =
                    process.env.SESSION_REFRESH_AFTER && process.env.SESSION_REFRESH_AFTER !== '0'
                        ? process.env.SESSION_REFRESH_AFTER
                        : 600;
                _.delay(() => {
                    ping();
                    try {
                        axios.post(route('auth.ping') + '?' + Date.now().toString());
                    } catch (error) {
                        console.error(error);
                    }
                }, 1000 * refreshSessionAfter);
            }

            ping();
        },
        setAppOverLay(overlay) {
            document.querySelector('html').style.overflow = overlay ? 'hidden' : 'unset';
            return (this.overlay = overlay);
        },
        setLocalStorage(data, action) {
            if (!data && data.item) {
                return;
            }
            switch (action) {
                case 'add':
                    return localStorage.setItem(data.item, JSON.stringify(data.data || new Object()));
                case 'remove':
                    return localStorage.removeItem(data.item);
                default:
                    return;
            }
        },
        removeEmpty(obj) {
            return Object.fromEntries(Object.values(obj).filter((v) => v != null));
        },
        simulateClick(el) {
            if (!el) {
                return;
            }
            const event = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            el.dispatchEvent(event);
        },
        simulateResize() {
            _.delay(() => {
                window.dispatchEvent(new Event('resize'));
            }, 300);
        },
        initMenuGroups() {
            const groupNodeList = this.$el.querySelectorAll('.menu-group.group');
            if (!groupNodeList.length) {
                return;
            }

            let groups = {};
            groupNodeList.forEach((menu) => {
                if (menu && menu.dataset && menu.dataset.menu) {
                    groups[menu.dataset.menu] = false;
                }
            });

            Object.assign(this.menuGroups, groups);

            const activeRoute = this.$el.querySelector('.v-list-item--active');
            const activeGroup =
                !!activeRoute && !!activeRoute.parentNode.dataset.menu ? activeRoute.parentNode.dataset.menu : false;

            if (activeGroup) {
                _.delay(
                    () => {
                        return this.toggleGroupMenu(activeGroup);
                    },
                    101,
                    'later'
                );
            }
        },
        toggleGroupMenu(menuGroup) {
            if (!this.menu) {
                return (this.menu = true);
            }
            this.menuGroups[menuGroup] = !this.menuGroups[menuGroup];
            return (this.adminMenu = !this.adminMenu);
        },
        setWrapSize(menuIsOpen) {
            const padding = menuIsOpen ? '0 0 0 250px' : '0 0 0 63px';
            const main = document.querySelector('.v-main');
            return main ? (main.style.padding = padding) : false;
        },
        setProfile(user) {
            if (user) {
                return this.$store.dispatch('user/login', user);
            }
            return this.$store.dispatch('user/logout');
        },
        setLoading(loading) {
            let loader = document.querySelector('.vld-overlay');
            if (loader) {
                loading ? loader.classList.add('is-active') : loader.classList.remove('is-active');
            }
        },
        setAllThemes() {
            this.$store.dispatch('themes/setAllTheme', this.themes);
        },
        setCurrentTheme() {
            // TODO check for each user!
            this.$store.dispatch('themes/setCurrentTheme', 0);
        },
        setNotificationsCount(count) {
            const notificaionsBadge = this.$el.querySelector('.v-avatar.notifications');
            if (notificaionsBadge && notificaionsBadge.querySelector('label')) {
                return (notificaionsBadge.querySelector('label').innerHTML = count || '0');
            }
        },
        changeTheme() {
            let theme = this.allThemes.find((x) => x.id === this.currentTheme);
            theme = theme ? theme : this.allThemes.find((x) => x.default);
            Object.keys(theme.dark).forEach((i) => {
                this.$vuetify.theme.themes.dark[i] = theme.dark[i];
            });
            Object.keys(theme.light).forEach((i) => {
                this.$vuetify.theme.themes.light[i] = theme.light[i];
            });
            this.$vuetify.theme.themes.id = theme.id;
            this.changeThemeStyles(theme.scss);
        },
        changeThemeStyles(file) {
            const styleFiles = document.querySelectorAll('#style');
            if (!styleFiles && !styleFiles.length) {
                return;
            }
            styleFiles.forEach((style) => {
                const currentFile = style.href.split('/').pop();
                const currentFilePath = style.href;
                if (file === currentFile) {
                    return;
                }
                this.setLoading(true);
                const newFilePath = currentFilePath.replace(currentFile, file);
                style.setAttribute('href', newFilePath);
                _.delay(
                    () => {
                        this.setLoading(false);
                    },
                    400,
                    'later'
                );
            });
        },
        parseErrorsAndNotify(error) {
            let errors = error.response.data.errors.map((e) => {
                return e.errors.join(' ');
            });

            this.notify('error', errors.join(' '));
        },
        notify(type, title = null, text, isBottom = false) {
            if (document.querySelector('.v-snack')) {
                document.querySelector('.v-snack').remove();
            }
            if (!text) {
                switch (type) {
                    case 'success':
                        text = title;
                        title = t('default::notification.success');
                        break;
                    case 'info':
                        text = title;
                        title = t('default::notification.info');
                        break;
                    case 'warning':
                        text = title;
                        title = t('default::notification.warning');
                        break;
                    case 'error':
                        text = title;
                        title = t('default::notification.error');
                        break;
                    default:
                        break;
                }
            }
            this.notifications.push({
                title: title,
                text: text,
                color: type,
                right: true,
                top: !isBottom,
                bottom: isBottom,
            });
        },
        moment: function () {
            return moment();
        },
        route: function (...params) {
            return route(...params);
        },
        md5(string) {
            return window.md5(string);
        },
        t(string, params) {
            return t(string, params);
        },
        goTo(link, newTab = false) {
            if (newTab) {
                return window.open(link, '_blank');
            }
            return (window.location.href = link);
        },
        themeConfig(property) {
            return this.themeOptions[property];
        },
        makeInitials(input) {
            if (input === '-' || input === '' || input === null) {
                input = t('default::avatar.default');
            }
            const name = input.split(' ');
            if (name.length === 1) {
                return name.join('').substring(0, 2);
            }
            return name
                .splice(0, 2)
                .map((x) => x.substring(0, 1))
                .join('');
        },
        randomColor() {
            let theme = this.allThemes.find((x) => x.id === this.currentTheme);
            if (!theme) {
                return 'primary';
            }
            const colors = Object.keys(theme.light);
            return colors[Math.floor(Math.random() * colors.length)];
        },
    },
});
